import React, { Component } from 'react'
import { bindActionCreators } from 'redux'
import { FormattedMessage, injectIntl } from 'react-intl'
import { connect } from 'react-redux'
import { push } from 'react-router-redux'
import Notifications from 'react-notification-system-redux'
import validator from 'validator'
import moment from 'moment'

import * as constants from '../constants'

import { Form, InputField, SelectField, CheckboxField, SubmitButton } from '../../../common/form'
import { url, handleCommonEditActions, validationMessage, getPrerequisitesArray } from '../../../common/helpers'
import { LoadingOverlay } from '../../../common/loading_overlay'

import * as companyNumberingsActionCreators from '../actionCreators'
import { createGetCompanyNumberingByIDSelector, createGetCurrentCompanyNumberingStatusSelector } from '../selectors'

import { preparePrerequisitesActionCreators } from '../../../common/prerequisites/actionCreators'
import { preparePrerequisitesFetchStatusSelectors } from '../../../common/prerequisites/selectors'
import PermissionsCheck from '../../auth/components/PermissionsCheck'
import { PERMS } from '../permissions'

const componentIdentifier = 'company_numberings_edit'
const prerequisites = ['companies']

class CompanyNumberingsEditForm extends Component {
    defaultValues = {}

    validationRules = {
        name: value => validator.isEmpty(String(value)) && validationMessage('isEmpty'),
    }

    state = {
        values: this.defaultValues,
    }

    setValues = (values, callback) => {
        this.setState(
            {
                values,
            },
            callback
        )
    }

    handleChangeValues = values => {
        this.setValues(values)
    }

    handleSubmit = values => {
        this.setValues(values, () => {
            this.handleSave()
        })
    }

    handleSave = () => {
        this.props.actions.saveCompanyNumbering(this.state.values)
    }

    successCallback = () => {
        this.props.successCallback ? this.props.successCallback() : this.props.redirect(url(this.props.match, 'company-numberings'))
    }

    backCallback = () => {
        this.props.backCallback ? this.props.backCallback() : this.props.redirect(url(this.props.match, 'company-numberings'))
    }

    componentDidMount() {
        this.props.companyNumberingID && this.props.actions.fetchCompanyNumbering(this.props.companyNumberingID)
        this.props.actions.fetchPrerequisites()

        if (this.props.defaultValues) {
            this.setValues(this.props.defaultValues)
        }
    }

    componentWillReceiveProps(nextProps) {
        handleCommonEditActions(this.props, nextProps, this.successCallback)

        if (
            nextProps.companyNumbering &&
            (JSON.stringify(nextProps.companyNumbering) !== JSON.stringify(this.props.companyNumbering) || !this.state.values.id)
        ) {
            this.setValues(nextProps.companyNumbering.toJS())
        }
    }

    render() {
        const perms = this.props.PERMS ? this.props.PERMS : PERMS
        const now = moment()
        const yearFormatOptions = [{ id: 'YYYY', name: now.format('YYYY') }, { id: 'YY', name: now.format('YY') }]
        const digitsOptions = [
            { id: '2', name: '99' },
            { id: '3', name: '999' },
            { id: '4', name: '9 999' },
            { id: '5', name: '99 999' },
            { id: '6', name: '999 999' },
        ]
        const typesOptions = [
            { id: String(constants.TYPE_ISSUED_INVOICES), name: this.props.intl.formatMessage({ id: 'companyNumberingTypes.1' }) },
            { id: String(constants.TYPE_RECEIVED_INVOICES), name: this.props.intl.formatMessage({ id: 'companyNumberingTypes.2' }) },
            { id: String(constants.TYPE_ISSUED_CREDIT_NOTES), name: this.props.intl.formatMessage({ id: 'companyNumberingTypes.3' }) },
            { id: String(constants.TYPE_RECEIVED_CREDIT_NOTES), name: this.props.intl.formatMessage({ id: 'companyNumberingTypes.4' }) },
        ]

        return (
            <div>
                <LoadingOverlay
                    active={this.props.prerequisites.status.get('fetching') || this.props.status.get('fetching') || this.props.status.get('saving')}
                >
                    <PermissionsCheck hasAny={Object.values(perms)}>
                        <Form
                            values={this.state.values}
                            validationRules={this.validationRules}
                            onChange={this.handleChangeValues}
                            onSubmit={this.handleSubmit}
                            isEdit={Boolean(this.props.companyNumbering)}
                        >
                            <div className={this.props.hiddenFields && this.props.hiddenFields.indexOf('company_id') !== -1 ? 'hidden' : ''}>
                                <SelectField
                                    id="company_id"
                                    label={this.props.intl.formatMessage({ id: 'fields.company' })}
                                    values={getPrerequisitesArray(this.props.prerequisites.values.get('companies'))}
                                />
                            </div>
                            <div className="row">
                                <div className="col-md-6">
                                    <InputField id="name" label={this.props.intl.formatMessage({ id: 'fields.name' })} />
                                </div>
                                <div className="col-md-6">
                                    <SelectField
                                        id="type_id"
                                        label={this.props.intl.formatMessage({ id: 'fields.type' })}
                                        values={typesOptions}
                                        prompt={false}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-3">
                                    <InputField id="prefix" label={this.props.intl.formatMessage({ id: 'fields.prefix' })} maxlength="4" />
                                </div>
                                <div className="col-md-3">
                                    <InputField id="separator" label={this.props.intl.formatMessage({ id: 'fields.separator' })} maxlength="1" />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6">
                                    <SelectField
                                        id="year_format"
                                        label={this.props.intl.formatMessage({ id: 'fields.yearFormat' })}
                                        values={yearFormatOptions}
                                        nullable
                                    />
                                </div>
                                <div className="col-md-6">
                                    <SelectField
                                        id="digits"
                                        label={this.props.intl.formatMessage({ id: 'fields.maxRange' })}
                                        values={digitsOptions}
                                    />
                                </div>
                            </div>
                            {this.state.values.year_format === 'YYYY' || this.state.values.year_format === 'YY' ? (
                                <CheckboxField id="month" label={this.props.intl.formatMessage({ id: 'fields.showMonthNumber' })} />
                            ) : null}
                            <InputField id="note" label={this.props.intl.formatMessage({ id: 'fields.note' })} />

                            <div className="btns-form">
                                <button onClick={this.backCallback} className="btn btn-addon btn-default">
                                    <i className="far fa-chevron-left" /> <FormattedMessage id="buttons.back" />
                                </button>
                                <SubmitButton
                                    perms={Object.values(perms)}
                                    isEdit={Boolean(this.props.companyNumbering)}
                                    className="btn-addon pull-right"
                                />
                            </div>
                        </Form>
                    </PermissionsCheck>
                </LoadingOverlay>
            </div>
        )
    }
}

function mapStateToProps(state, ownProps) {
    return {
        companyNumbering: createGetCompanyNumberingByIDSelector(ownProps.companyNumberingID)(state),
        status: createGetCurrentCompanyNumberingStatusSelector()(state),
        prerequisites: preparePrerequisitesFetchStatusSelectors(componentIdentifier, prerequisites, state),
    }
}

function mapDispatchToProps(dispatch) {
    return {
        actions: {
            ...bindActionCreators(
                {
                    ...companyNumberingsActionCreators,
                    ...preparePrerequisitesActionCreators(componentIdentifier, prerequisites),
                },
                dispatch
            ),
        },
        redirect: toURL => dispatch(push(toURL)),
        notify: (notification, type) => dispatch(Notifications.show(notification, type)),
    }
}

export default injectIntl(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(CompanyNumberingsEditForm)
)
